import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { RailSelection } from "@compositions/index";

// URL search-param names for Board focus, scoped to /workflows/:id. Kept
// in lockstep with `buildWorkflowSearch`, which writes them on cross-
// workflow navigation.
export const ITEM_PARAM = "item";
export const THREAD_PARAM = "thread";
export const COMPOSE_PARAM = "compose";
export const COMPOSE_TASK = "task";

export interface SelectionWrite {
  readonly itemId?: RailSelection;
  readonly threadId?: string | null;
}

/** Read and write the board's URL selection. Shared by the body and the scene. */
export function useBoardSelection(): {
  readonly selectedItemId: RailSelection;
  readonly selectedThreadId: string | null;
  readonly composeTask: boolean;
  readonly writeSelection: (next: SelectionWrite, options?: { replace?: boolean }) => void;
  readonly clearCompose: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedItemId: RailSelection = useMemo(() => {
    const value = searchParams.get(ITEM_PARAM) ?? "";
    return value.length > 0 ? value : null;
  }, [searchParams]);
  const selectedThreadId: string | null = useMemo(() => {
    const value = searchParams.get(THREAD_PARAM) ?? "";
    return value.length > 0 ? value : null;
  }, [searchParams]);
  const composeTask = searchParams.get(COMPOSE_PARAM) === COMPOSE_TASK;
  const writeSelection = useCallback(
    (next: SelectionWrite, options?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.itemId !== undefined) {
            if (next.itemId === null) params.delete(ITEM_PARAM);
            else params.set(ITEM_PARAM, next.itemId);
          }
          if (next.threadId !== undefined) {
            if (next.threadId === null) params.delete(THREAD_PARAM);
            else params.set(THREAD_PARAM, next.threadId);
          }
          return params;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );
  const clearCompose = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.has(COMPOSE_PARAM)) return prev;
        const params = new URLSearchParams(prev);
        params.delete(COMPOSE_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  return { selectedItemId, selectedThreadId, composeTask, writeSelection, clearCompose };
}
